import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { smsApi } from '../../api/smsApi';
import { SoftCard } from '../../ui/SoftCard';

function fmtDateTR(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR');
}

function normalizePhoneInput(value) {
  return (value ?? '').replace(/\D/g, '');
}

export function SmsTabBlockedList() {
  const [rows, setRows] = useState([]);
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removingPhone, setRemovingPhone] = useState('');
  const [error, setError] = useState('');

  async function loadBlockedNumbers() {
    setLoading(true);
    setError('');
    try {
      const res = await smsApi.getBlockedNumbers();
      setRows(Array.isArray(res) ? res : []);
    } catch (err) {
      setRows([]);
      setError(err?.message || 'Engellenen numaralar alınamadı.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBlockedNumbers();
  }, []);

  async function handleAdd() {
    if (!phone.trim()) return;

    setSaving(true);
    setError('');

    try {
      await smsApi.addBlockedNumber({
        phone: phone.trim(),
        note: note.trim() || null,
      });

      setPhone('');
      setNote('');
      await loadBlockedNumbers();
    } catch (err) {
      setError(err?.message || 'Numara eklenemedi.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(rowPhone) {
    if (!rowPhone) return;

    setRemovingPhone(rowPhone);
    setError('');

    try {
      await smsApi.removeBlockedNumber(rowPhone);
      setRows((prev) => prev.filter((x) => x.phone !== rowPhone));
    } catch (err) {
      setError(err?.message || 'Numara silinemedi.');
    } finally {
      setRemovingPhone('');
    }
  }

  return (
    <MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SoftCard>
            <MDBox p={2.2}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={1.2}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="bold">
                    Engellenen Numaralar
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Buradaki numaralara sistem SMS göndermez.
                  </MDTypography>
                </MDBox>

                <MDButton
                  variant="outlined"
                  color="dark"
                  startIcon={<Icon>refresh</Icon>}
                  onClick={loadBlockedNumbers}
                  disabled={loading}
                >
                  Yenile
                </MDButton>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {error ? (
                <MDBox mb={2}>
                  <Alert severity="error">{error}</Alert>
                </MDBox>
              ) : null}

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    placeholder="05551234567"
                    value={phone}
                    onChange={(e) => setPhone(normalizePhoneInput(e.target.value))}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Not"
                    placeholder="İptal talebi, manuel engel vb."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <MDButton
                    fullWidth
                    variant="gradient"
                    color="dark"
                    onClick={handleAdd}
                    disabled={saving || !phone.trim()}
                    sx={{ height: '100%' }}
                  >
                    {saving ? 'Ekleniyor...' : 'Ekle'}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </SoftCard>
        </Grid>

        <Grid item xs={12}>
          <SoftCard>
            <MDBox p={2.2}>
              <MDTypography variant="h6" fontWeight="bold" mb={2}>
                Engellenenler Listesi
              </MDTypography>

              {loading ? (
                <MDBox minHeight="140px" display="flex" alignItems="center" justifyContent="center">
                  <CircularProgress size={28} />
                </MDBox>
              ) : rows.length === 0 ? (
                <Alert severity="info">Henüz engellenmiş numara yok.</Alert>
              ) : (
                <Grid container spacing={1.5}>
                  {rows.map((row) => {
                    const deleting = removingPhone === row.phone;

                    return (
                      <Grid item xs={12} key={row.id ?? row.phone}>
                        <Card
                          sx={{
                            boxShadow: 'none',
                            border: '1px solid #eee',
                            borderRadius: '14px',
                          }}
                        >
                          <MDBox
                            p={1.6}
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            flexDirection={{ xs: 'column', md: 'row' }}
                            gap={1.2}
                          >
                            <MDBox>
                              <MDTypography variant="button" fontWeight="bold">
                                {row.phone || '—'}
                              </MDTypography>

                              <MDBox mt={0.5}>
                                <MDTypography variant="caption" color="text.secondary">
                                  Eklenme: {fmtDateTR(row.createdAt)}
                                </MDTypography>
                              </MDBox>

                              {row.note ? (
                                <MDBox mt={0.5}>
                                  <MDTypography variant="caption" color="text.secondary">
                                    Not: {row.note}
                                  </MDTypography>
                                </MDBox>
                              ) : null}
                            </MDBox>

                            <MDBox>
                              <IconButton
                                color="error"
                                onClick={() => handleRemove(row.phone)}
                                disabled={deleting}
                              >
                                <Icon>{deleting ? 'hourglass_top' : 'delete'}</Icon>
                              </IconButton>
                            </MDBox>
                          </MDBox>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </MDBox>
          </SoftCard>
        </Grid>
      </Grid>
    </MDBox>
  );
}
